import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Download, Send } from "lucide-react";
import BaseModal from "../modals/BaseModal";
import { pipelineService } from "../../../../services/pipelineService";

const formatInr = (n) => {
  const value = Number(n);
  if (!Number.isFinite(value)) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(value);
};

const formatDateLabel = (iso) => {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "long",
      day: "2-digit",
    }).format(d);
  } catch {
    return "—";
  }
};

function PillInput({ value }) {
  return (
    <span className="inline-flex items-center rounded-lg bg-white border border-gray-200 px-3 py-1.5 text-sm font-semibold text-gray-800 shadow-sm tabular-nums">
      {value}
    </span>
  );
}

function signatureInnerClassName({ isSigned, disabled, interactive }) {
  return [
    "mt-2 w-full rounded-lg border border-dashed px-4 py-3 text-sm font-semibold",
    interactive
      ? disabled
        ? "bg-white/70 border-emerald-100 text-emerald-400 cursor-not-allowed"
        : "bg-white border-emerald-200 text-emerald-700 hover:bg-emerald-100"
      : isSigned
        ? "bg-emerald-100 border-emerald-200 text-emerald-800"
        : "bg-white/70 border-emerald-100 text-emerald-400",
  ].join(" ");
}

function SignatureBox({ label, signedBy, onSign, disabled, ctaLabel }) {
  const isSigned = Boolean(signedBy);
  const showSignButton = Boolean(onSign) && !isSigned;

  return (
    <div className="flex-1 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 contract-print-signature">
      <div className="text-sm font-semibold text-emerald-800">{label}</div>
      {showSignButton ? (
        <button
          type="button"
          disabled={disabled}
          onClick={onSign}
          className={signatureInnerClassName({ isSigned: false, disabled, interactive: true })}
        >
          {ctaLabel || "Click to sign"}
        </button>
      ) : (
        <div
          className={signatureInnerClassName({
            isSigned,
            disabled: false,
            interactive: false,
          })}
        >
          {isSigned ? `${signedBy} signed` : "Awaiting signature"}
        </div>
      )}
    </div>
  );
}

/**
 * Enterprise-ready demo contract modal.
 * - Sends contract from brand side (brand signature + payment term)
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {{ id: string, name: string } | null} props.influencer
 * @param {string} props.brandName
 * @param {string} props.campaignName
 * @param {() => void} props.onClose
 * @param {(text: string) => void} props.onAddActivity
 * @param {(updatedItem: any) => void} [props.onUpdated]
 */
export default function ContractModal({
  open,
  influencer,
  brandName,
  campaignName,
  onClose,
  onAddActivity,
  onUpdated,
}) {
  const [saving, setSaving] = useState(false);
  const [paymentTermDays, setPaymentTermDays] = useState(10);

  const contract = useMemo(() => influencer?.contract || null, [influencer?.contract]);
  const negotiated = useMemo(() => influencer?.negotiation || null, [influencer?.negotiation]);

  const deliverables = useMemo(() => {
    const text = String(contract?.deliverablesText || "").trim();
    if (text) {
      const chunks = text.split(/\s*\+\s*/).map((p) => p.trim()).filter(Boolean);
      return chunks.length ? chunks : [text];
    }
    const d = negotiated?.deliverables || {};
    const reels = Number(d.reels || 0);
    const stories = Number(d.stories || 0);
    const posts = Number(d.posts || 0);
    const parts = [];
    if (reels) parts.push(`${reels} ${reels === 1 ? "Instagram Reel" : "Instagram Reels"}`);
    if (stories) parts.push(`${stories} ${stories === 1 ? "Story" : "Stories"}`);
    if (posts) parts.push(`${posts} ${posts === 1 ? "Post" : "Posts"}`);
    if (parts.length > 0) return parts;
    return ["—"];
  }, [contract?.deliverablesText, negotiated?.deliverables]);

  const brandSignedBy = String(contract?.brandSignedBy || "").trim();
  const influencerSignedBy = String(contract?.influencerSignedBy || "").trim();
  const brandSigned = Boolean(contract?.brandSignedAt);
  const influencerSigned = Boolean(contract?.influencerSignedAt);
  const fullySigned = brandSigned && influencerSigned;
  const [sentToInfluencer, setSentToInfluencer] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (brandSigned) return;
    const fromContract = contract?.paymentTermDays;
    const next =
      fromContract != null && Number.isFinite(Number(fromContract))
        ? Number(fromContract)
        : 10;
    setPaymentTermDays(next);
  }, [open, brandSigned, contract?.paymentTermDays]);

  /** Print only the contract: clone to body so we can hide #root and avoid duplicate pages. */
  useEffect(() => {
    if (!open) return undefined;

    const removeClone = () => {
      document.getElementById("contract-print-clone")?.remove();
    };

    const cloneForPrint = () => {
      const source = document.getElementById("contract-print-area");
      if (!source) return;
      removeClone();
      const clone = source.cloneNode(true);
      clone.id = "contract-print-clone";
      document.body.appendChild(clone);
    };

    window.addEventListener("beforeprint", cloneForPrint);
    window.addEventListener("afterprint", removeClone);
    return () => {
      window.removeEventListener("beforeprint", cloneForPrint);
      window.removeEventListener("afterprint", removeClone);
      removeClone();
    };
  }, [open]);

  const onDownload = useCallback(() => {
    onAddActivity?.("Contract: download requested");
    window.requestAnimationFrame(() => {
      window.print();
    });
  }, [onAddActivity]);

  const footer = (
    <div className="flex items-center justify-end gap-2 lg:gap-3">
      <button
        type="button"
        onClick={onDownload}
        className="px-2 lg:px-4 py-1 lg:py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-gray-800 text-xs md:text-sm font-medium hover:bg-gray-100 inline-flex items-center gap-2"
      >
        <Download className="w-4 h-4" />
        Download PDF
      </button>
      {(fullySigned || !sentToInfluencer) && (
        <button
          type="button"
          disabled={
            !influencer?.__pipelineItemId ||
            saving ||
            (!fullySigned && !brandSigned)
          }
          onClick={async () => {
            const itemId = influencer?.__pipelineItemId;
            if (!itemId) return;
            if (fullySigned) {
              setSaving(true);
              try {
                const updated = await pipelineService.confirmContract({ itemId });
                onAddActivity?.("Contract: confirmed");
                onUpdated?.(updated);
                onClose?.();
              } finally {
                setSaving(false);
              }
              return;
            }

            onAddActivity?.("Contract: sent to influencer");
            setSentToInfluencer(true);
            onClose?.();
          }}
          className={[
            "px-2 lg:px-4 py-1 lg:py-2.5 rounded-xl text-xs md:text-sm font-semibold inline-flex items-center gap-2 transition-colors",
            saving ||
            !influencer?.__pipelineItemId ||
            (!fullySigned && !brandSigned)
              ? "bg-gray-200 text-gray-500 cursor-not-allowed"
              : fullySigned
                ? "bg-[#1D9E75] text-white hover:bg-[#178862] shadow-sm"
                : "bg-[#E65C1A] text-white hover:bg-[#D14F12] shadow-sm",
          ].join(" ")}
        >
          {fullySigned ? (
            <>
              <span className="text-base leading-none" aria-hidden>
                ✓
              </span>
              {saving ? "Confirming…" : "Mark confirmed"}
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              {saving ? "Sending…" : "Send contract"}
            </>
          )}
        </button>
      )}
    </div>
  );

  const printTitle = `Contract — ${influencer?.name ?? "Influencer"}`;

  return (
    <BaseModal
      open={open}
      title={printTitle}
      onClose={onClose}
      footer={footer}
      className="max-w-[760px] print:max-w-none"
    >
      <style>
        {`
@media print {
  @page { margin: 10mm; size: auto; }
  html, body {
    margin: 0 !important;
    padding: 0 !important;
    height: auto !important;
    background: #fff !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  body > *:not(#contract-print-clone) {
    display: none !important;
  }
  #contract-print-clone {
    display: block !important;
    width: 100% !important;
    max-width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
    box-shadow: none !important;
    border: none !important;
    overflow: visible !important;
  }
  #contract-print-clone .contract-print-unified {
    break-inside: avoid;
    page-break-inside: avoid;
  }
}
`}
      </style>
      <div id="contract-print-area" className="px-4 py-2 sm:px-5 sm:py-6 bg-white">
        <div className="contract-print-unified">
        <h2 className="hidden print:block text-lg font-extrabold text-gray-900 mb-3 border-b border-gray-200 pb-2">
          {printTitle}
        </h2>
        {fullySigned ? (
          <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800">
            ✓ Contract fully executed — both parties have signed
          </div>
        ) : brandSigned ? (
          <div className="mb-4 rounded-xl border border-amber-200 bg-[#fff9e6] px-4 py-3 text-sm font-semibold text-amber-900">
            Brand signed — ready to send to influencer
          </div>
        ) : (
          <div className="mb-4 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-800">
            Choose payment term and sign the contract as the brand.
          </div>
        )}

        <div className="rounded-xl border border-gray-200 bg-[#F3F1EA] px-4 py-4 sm:px-5 sm:py-6">
          <div className="text-center text-sm lg:text-base font-extrabold text-gray-900 tracking-wide">
            INFLUENCER COLLABORATION AGREEMENT
          </div>

          <div className="mt-4 text-xs lg:text-sm text-gray-700 leading-relaxed">
            This agreement is between{" "}
            <span className="font-extrabold text-gray-900">{brandName}</span>{" "}
            ("Brand") and{" "}
            <span className="font-extrabold text-gray-900">
              {influencer?.name ?? "Influencer"}
            </span>{" "}
            ("Influencer") for the{" "}
            <span className="font-extrabold text-gray-900">{campaignName}</span>{" "}
            campaign.
          </div>

          <div className="mt-5 space-y-3 text-gray-800">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-extrabold">Deliverables:</span>
              {deliverables.map((d, idx) => (
                <React.Fragment key={`${d}-${idx}`}>
                  {idx > 0 ? (
                    <span className="text-gray-500 font-semibold">+</span>
                  ) : null}
                  <PillInput value={d} />
                </React.Fragment>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="font-extrabold">Go-live date:</span>
              <PillInput value={formatDateLabel(influencer?.startDate || influencer?.goLiveISO || contract?.goLiveISO)} />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="font-extrabold">Compensation:</span>
              <PillInput value={formatInr(contract?.agreedRateInr)} />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="font-extrabold">Payment term:</span>
              {brandSigned ? (
                <PillInput
                  value={
                    contract?.paymentTermDays
                      ? `${Number(contract.paymentTermDays)} days`
                      : `${paymentTermDays} days`
                  }
                />
              ) : (
                <select
                  value={paymentTermDays}
                  onChange={(e) => setPaymentTermDays(Number(e.target.value))}
                  className="rounded-lg bg-white border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-800 shadow-sm"
                >
                  {[5, 10, 15, 20, 30].map((d) => (
                    <option key={d} value={d}>
                      {d} days
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div className="mt-6 italic text-gray-600 font-semibold">
            Both parties agree to the terms above.
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <SignatureBox
            label="Influencer signature"
            signedBy={influencerSignedBy}
          />
          <SignatureBox
            label={`Brand signature (${brandName})`}
            signedBy={brandSignedBy || (brandSigned ? brandName : "")}
            disabled={!influencer?.__pipelineItemId || saving}
            ctaLabel={saving ? "Signing…" : "Click to sign"}
            onSign={async () => {
              const itemId = influencer?.__pipelineItemId;
              if (!itemId || saving || brandSigned) return;
              setSaving(true);
              try {
                // Persist the chosen payment term before brand signs.
                await pipelineService.generateContract({
                  itemId,
                  paymentTermDays,
                });
                const signed = await pipelineService.signContractAsBrand({
                  itemId,
                  signedBy: brandName,
                });
                onAddActivity?.(`Contract: brand signed (${brandName})`);
                onUpdated?.(signed);
              } finally {
                setSaving(false);
              }
            }}
          />
        </div>
        </div>
      </div>
    </BaseModal>
  );
}

